import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import DropdownSelect from 'features/DropdownSelect/DropdownSelect'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { AllIcons } from 'helpers/allIcons'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import {
    getLanguages,
    setLanguage
} from 'services/endpoints/settings/general/language'
import { useUserProfile } from 'store'
import { type ILanguage } from 'types/interfaces/ITypes'

interface LanguagesResponse {
    message: Record<string, ILanguage>
}

export const EditLanguage = () => {
    const userData = useUserProfile()!

    const [searchTerm, setSearchTerm] = useState('')
    const [selectedLanguageId, setSelectedLanguageId] = useState<number>(
        Number(userData.language)
    )

    const queryClient = useQueryClient()
    const { t, i18n } = useTranslation()

    const { data } = useQuery<LanguagesResponse>(
        'allLanguages',
        getLanguages,
        {
            refetchOnWindowFocus: false
        }
    )

    const languages = useMemo(
        () => (data ? Object.values(data.message) : []),
        [data]
    )

    const selectedLanguage = useMemo(
        () =>
            languages.find(
                language => language.id === selectedLanguageId
            ),
        [languages, selectedLanguageId]
    )

    const filteredLanguages = useMemo(() => {
        if (!searchTerm) return languages

        const search = searchTerm.toLowerCase()

        return languages.filter(language =>
            language.name.toLowerCase().includes(search)
        )
    }, [languages, searchTerm])

    const setLanguageMutation = useMutation(
        async () => {
            if (!selectedLanguage) return

            return await setLanguage(selectedLanguage.id)
        },
        {
            onSuccess: () => {
                if (!selectedLanguage) return

                i18n.changeLanguage(
                    selectedLanguage.code.toLowerCase()
                )

                queryClient.invalidateQueries('user-profile')
            },
            onError: () => {
                addToast('error', 'Something went wrong')
            }
        }
    )

    return (
        <WithHeaderSection
            headerSection={
                <LayoutHeader
                    type='basic'
                    section={t('general')}
                    title={t('language')}
                />
            }
        >
            <DropdownSelect
                icon={AllIcons.globe_black}
                placeHolder={t('selectLanguage')}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                elementsArr={filteredLanguages}
                topSectionSelected={selectedLanguage}
                topSectionSelect={(language: ILanguage) => {
                    setSelectedLanguageId(language.id)
                }}
            />

            <Button
                text={t('setLanguage')}
                color='black'
                font='mont-16-bold'
                height='5'
                width='20'
                customClass='settings__addlanguage'
                clickFn={() => {
                    setLanguageMutation.mutate()
                }}
                disabled={!selectedLanguage}
            />
        </WithHeaderSection>
    )
}